import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../../components/AnimatedCol";
import ConvertMeasurement from "../../../../components/Math/Conversion/ConvertMeasurement";
import ConvertUnit from "../../../../components/Math/Conversion/ConvertUnit";
import { ImperialUnits, MetricUnits } from "../../../../components/Math/Units/AllUnits";
import Inch from "../../../../components/Math/Units/Imperial/Inch";
import InchSquared from "../../../../components/Math/Units/Imperial/InchSquared";
import Kilopound from "../../../../components/Math/Units/Imperial/Kilopound";
import KilopoundPerSquareInch from "../../../../components/Math/Units/Imperial/KilopoundPerSquareInch";
import KiloNewton from "../../../../components/Math/Units/Metric/KiloNewton";
import Megapascal from "../../../../components/Math/Units/Metric/Megapascal";
import Millimetre from "../../../../components/Math/Units/Metric/Millimetre";
import MillimetreSquared from "../../../../components/Math/Units/Metric/MillimetreSquared";
import { NumberFormatText } from "../../../../components/NumberFormatText";
import { RowBody } from "../../../../components/RowValue/RowBody";
import { useGetCarryingElementQuery } from "../../../../services/API/titanApi";
import {
  selectAreaOfSteel,
  selectBarOD,
  selectFy,
  selectFyControlling,
  selectPy,
  selectedCarryingElement,
  setAreaOfSteel,
  setBarOD,
  setFy,
  setFyControlling,
  setPy,
} from "../../../../store/slices/aashtoSlice";
import { Measurements, selectMeasurementType } from "../../../../store/slices/settingsSlice";

function BarData({ isVisible }) {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();
  const carryingElement = useSelector(selectedCarryingElement);
  const { data: carryingElementData } = useGetCarryingElementQuery(carryingElement?.name);

  let fyResult =
    carryingElementData?.CharacteristicLoadCarryingCapacity / carryingElementData?.CrossSectArea;

  let FIX_VALUE = 87;
  let fyControllingResult = fyResult > FIX_VALUE ? FIX_VALUE : fyResult;

  const measurementType = useSelector(selectMeasurementType);

  const barOD = useSelector(selectBarOD);

  const areaOfSteel = useSelector(selectAreaOfSteel);

  const fy = useSelector(selectFy);
  const py = useSelector(selectPy);
  const fyControlling = useSelector(selectFyControlling);

  useEffect(() => {
    if (measurementType === Measurements.METRIC) {
      dispatch(
        setBarOD({
          value: ConvertMeasurement({
            userInput: carryingElementData?.OutsideDiameter,
            from: ImperialUnits.Inch,
          }),
          unit: MetricUnits.Millimetre,
        })
      );

      dispatch(
        setAreaOfSteel({
          value: ConvertMeasurement({
            userInput: carryingElementData?.CrossSectArea,
            from: ImperialUnits.InchSquared,
          }),
          unit: MetricUnits.MillimetreSquared,
        })
      );

      dispatch(
        setFy({
          value: ConvertMeasurement({
            userInput: fyResult,
            from: ImperialUnits.KilopoundPerSquareInch,
          }),
          unit: MetricUnits.Megapascal,
        })
      );

      dispatch(
        setPy({
          value: ConvertMeasurement({
            userInput: carryingElementData?.CharacteristicLoadCarryingCapacity,
            from: ImperialUnits.Kilopound,
          }),
          unit: MetricUnits.KiloNewton,
        })
      );

      dispatch(
        setFyControlling({
          value: ConvertMeasurement({
            userInput: fyControllingResult,
            from: ImperialUnits.KilopoundPerSquareInch,
          }),

          unit: MetricUnits.Megapascal,
        })
      );
    }

    if (measurementType === Measurements.IMPERIAL) {
      dispatch(
        setBarOD({
          value: carryingElementData?.OutsideDiameter,
          unit: ImperialUnits.Inch,
        })
      );
      dispatch(
        setAreaOfSteel({
          value: carryingElementData?.CrossSectArea,
          unit: ImperialUnits.InchSquared,
        })
      );
      dispatch(setFy({ value: fyResult, unit: ImperialUnits.KilopoundPerSquareInch }));
      dispatch(
        setPy({
          value: carryingElementData?.CharacteristicLoadCarryingCapacity,
          unit: ImperialUnits.Kilopound,
        })
      );
      dispatch(
        setFyControlling({
          value: fyControllingResult,
          unit: ImperialUnits.KilopoundPerSquareInch,
        })
      );
    }
  }, [measurementType, carryingElementData, fyResult, fyControllingResult]);

  return (
    <AnimatedCol expand={isVisible}>
      <RowBody
        titleValue={<p>{t("aashto.inner.Bar OD")}</p>}
        inputValue={
          <span className="input">
            <NumberFormatText value={barOD.current} decimalScale={2} />
          </span>
        }
        unitValue={<ConvertUnit Metric={<Millimetre />} Imperial={<Inch />} />}
      />
      <RowBody
        titleValue={<p>{t("aashto.inner.Area of Steel")}</p>}
        indicesValue={
          <span>
            A<sub>b</sub>
          </span>
        }
        inputValue={
          <span className="input">
            <NumberFormatText value={areaOfSteel.current} decimalScale={2} />
          </span>
        }
        unitValue={<ConvertUnit Metric={<MillimetreSquared />} Imperial={<InchSquared />} />}
      />
      <RowBody
        titleValue={<p>{t("aashto.inner.Yield strength")}</p>}
        indicesValue={
          <span>
            F<sub>y</sub>
          </span>
        }
        inputValue={
          <span className="input">
            <NumberFormatText value={fy.current} decimalScale={2} />
          </span>
        }
        unitValue={<ConvertUnit Metric={<Megapascal />} Imperial={<KilopoundPerSquareInch />} />}
      />
      <RowBody
        titleValue={<p>{t("aashto.inner.Yield Load")}</p>}
        indicesValue={
          <span>
            P<sub>y</sub>
          </span>
        }
        inputValue={
          <span className="input">
            <NumberFormatText value={py.current} decimalScale={2} />
          </span>
        }
        unitValue={<ConvertUnit Metric={<KiloNewton />} Imperial={<Kilopound />} />}
      />
      <RowBody
        titleValue={<p>{t("aashto.inner.Fy(Controlling)")}</p>}
        indicesValue={
          <span>
            F<sub>y,c</sub>
          </span>
        }
        inputValue={
          <span className="input">
            <NumberFormatText value={fyControlling.current} decimalScale={2} />
          </span>
        }
        unitValue={<ConvertUnit Metric={<Megapascal />} Imperial={<KilopoundPerSquareInch />} />}
      />
    </AnimatedCol>
  );
}

export default BarData;
